<template>
  <v-card class="elevation-12">
    <v-toolbar color="primary" dark>
      Клиентские организации
    </v-toolbar>

    <v-sheet color="grey lighten-4" class="px-4 py-3" tile>
      <v-row>
        <v-col cols="12" sm="6" md="4">
          <v-autocomplete
              v-model="selectedOrganization"
              :items="distinctOrganizations"
              label="Фильтр по названию организации"
              prepend-inner-icon="mdi-filter"
              dense
              outlined
              hide-details
              clearable
          ></v-autocomplete>
        </v-col>
      </v-row>
    </v-sheet>

    <v-data-table
        :items="filteredOrganizations"
        :headers="headers"
        :loading="loading"
        group-by="OrganizationName"
        disable-pagination
        hide-default-footer
        class="elevation-1"
    >
      <template v-slot:group.header="{ group, headers, toggle, isOpen }">
        <td :colspan="headers.length" class="group-header-row" @click="toggle">
          <v-btn icon small class="mr-2">
            <v-icon>{{ isOpen ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
          </v-btn>
          <strong style="font-size: 15px;">{{ group || 'Без названия' }}</strong>

          <v-chip class="ml-3" small color="primary" outlined>
            Филиалов: {{ getBranchesCount(group) }}
          </v-chip>

          <v-btn
              v-if="getBranchesCount(group) > 1"
              small
              color="secondary"
              class="ml-4"
              @click.stop="onJoinButtonClicked(group)"
          >
            <v-icon left small>mdi-call-merge</v-icon> Объединить
          </v-btn>

          <v-btn
              v-if="hasGroup(group)"
              small
              color="error"
              class="ml-2"
              @click.stop="onDisbandGroupButtonClicked(group)"
          >
            <v-icon left small>mdi-trash-can-outline</v-icon> Расформировать
          </v-btn>
        </td>
      </template>

      <template v-slot:item.UnitName="{item}">
        {{ item.UnitName }}

        <v-chip
            v-if="item.GroupName && !item.MergedIntoId"
            x-small
            color="success"
            text-color="white"
            class="ml-2"
            title="Название объединенной группы"
        >
          Группа: {{ item.GroupName }}
        </v-chip>

        <v-chip
            v-if="item.MergedIntoId"
            close
            @click:close="onUnjoinButtonClicked(item)"
            x-small
            color="grey"
            text-color="white"
            class="ml-2"
            title="Отвязать филиал"
        >
          Объединен с: {{ getMasterBranchName(item.MergedIntoId) }}
        </v-chip>
      </template>

      <template v-slot:item.ContractDateFrom="{item}">
        <template v-if="item.ContractDateFrom">
          {{ formatDate(item.ContractDateFrom) }}
        </template>
        <template v-else>
          <v-icon color="grey">mdi-calendar-remove</v-icon>
        </template>
      </template>

      <template v-slot:item.ContractDateTo="{item}">
        <template v-if="item.ContractDateTo">
          {{ formatDate(item.ContractDateTo) }}
        </template>
        <template v-else>
          <v-icon color="grey">mdi-calendar-remove</v-icon>
        </template>
      </template>

      <template v-slot:item.actions="{item}">
        <div class="d-flex justify-end">
          <v-btn icon @click="onEditButtonClicked(item)" title="Редактировать">
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
        </div>
      </template>
    </v-data-table>

    <v-dialog v-model="editDialog" :persistent="true" width="600px">
      <EditClientOrganizationComponent
          ref="editOrganization"
          @cancel="onEditCanceled"
          @updated="onEditSuccess"
      />
    </v-dialog>

    <v-dialog v-model="joinDialog" width="600px" persistent>
      <v-card :loading="joinLoading">
        <v-toolbar color="secondary" dark flat>
          <v-toolbar-title>Слияние филиалов</v-toolbar-title>
        </v-toolbar>
        <v-card-text class="pt-5">
          <p>Выберите главный филиал, в который будут объединены остальные.</p>

          <v-autocomplete
              v-model="joinForm.masterBranchId"
              :items="currentJoinGroup"
              item-text="UnitName"
              item-value="Id"
              label="Главный филиал (останется активным)"
              outlined
              dense
          ></v-autocomplete>

          <v-autocomplete
              v-model="joinForm.branchIdsToJoin"
              :items="availableBranchesToJoin"
              item-text="UnitName"
              item-value="Id"
              label="Филиалы для слияния (будут привязаны к главному)"
              multiple
              chips
              outlined
              dense
              :disabled="!joinForm.masterBranchId"
          ></v-autocomplete>

          <v-text-field
              v-model="joinForm.groupName"
              label="Название группы (Отображается в статистике)"
              placeholder="Например: Центральный аппарат + Филиалы"
              outlined
              dense
              class="mt-2"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="joinDialog = false">Отмена</v-btn>
          <v-btn color="secondary" @click="submitJoin" :disabled="!isJoinValid">Объединить</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="disbandDialog" width="500px" persistent>
      <v-card :loading="disbandLoading">
        <v-toolbar color="error" dark flat>
          <v-toolbar-title>Расформировать группу</v-toolbar-title>
        </v-toolbar>
        <v-card-text class="pt-5">
          <p>Вы действительно хотите расформировать группу <strong>{{ groupToDisband }}</strong>?</p>
          <p class="mb-0">Все привязанные филиалы станут независимыми, а название группы будет удалено.</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="disbandDialog = false">Отмена</v-btn>
          <v-btn color="error" @click="submitDisband">Расформировать</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="unjoinDialog" width="500px" persistent>
      <v-card :loading="unjoinLoading">
        <v-toolbar color="warning" dark flat>
          <v-toolbar-title>Отвязать филиал</v-toolbar-title>
        </v-toolbar>
        <v-card-text class="pt-5">
          <p>Вы действительно хотите отвязать филиал <strong>{{ branchToUnjoin ? branchToUnjoin.UnitName : '' }}</strong>?</p>
          <p class="mb-0">Статистика по нему снова будет рассчитываться отдельно.</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="unjoinDialog = false">Отмена</v-btn>
          <v-btn color="warning" @click="submitUnjoin">Отвязать</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="showSuccessMessage" :timeout="5000" :top="true" color="success">
      Операция выполнена успешно.
    </v-snackbar>
  </v-card>
</template>

<script>
import {LAYOUT_LOADING_START, LAYOUT_LOADING_STOP} from "@/store/modules/layout/actions";
import ClientOrganizationService from "@/services/organization.service";
import EditClientOrganizationComponent from "../../components/organization/EditClientOrganizationComponent";
import moment from "moment";

export default {
  name: "ClientOrganizationsPage",
  components: {
    EditClientOrganizationComponent
  },
  data() {
    return {
      loading: false,
      organizations: [],
      selectedOrganization: null,
      editDialog: false,
      showSuccessMessage: false,

      // Join Modal State
      joinDialog: false,
      joinLoading: false,
      currentJoinGroup: [],
      joinForm: {
        masterBranchId: null,
        branchIdsToJoin: [],
        groupName: ''
      },

      // Unjoin Modal State
      unjoinDialog: false,
      unjoinLoading: false,
      branchToUnjoin: null,

      disbandDialog: false,
      disbandLoading: false,
      groupToDisband: null
    }
  },
  computed: {
    distinctOrganizations() {
      const names = this.organizations.map(o => o.OrganizationName).filter(Boolean);
      return [...new Set(names)].sort();
    },
    filteredOrganizations() {
      if (!this.selectedOrganization) {
        return this.organizations;
      }
      return this.organizations.filter(o => o.OrganizationName === this.selectedOrganization);
    },
    availableBranchesToJoin() {
      return this.currentJoinGroup.filter(branch => branch.Id !== this.joinForm.masterBranchId);
    },
    isJoinValid() {
      return this.joinForm.masterBranchId && this.joinForm.branchIdsToJoin.length > 0;
    },
    headers() {
      return [
        { text: 'ID', align: 'center', sortable: false, value: 'Id' },
        { text: 'Название организации', align: 'left', sortable: false, value: 'OrganizationName' },
        { text: 'Подразделение (Филиал)', align: 'left', sortable: false, value: 'UnitName' },
        { text: 'ИНН', align: 'center', sortable: false, value: 'Inn' },
        { text: 'Расчетный счет', align: 'center', sortable: false, value: 'AccountNumber' },
        { text: 'МФО', align: 'center', sortable: false, value: 'Mfo' },
        { text: 'Номер договора', align: 'center', sortable: false, value: 'ContractNumber' },
        { text: 'Дата (с)', align: 'center', sortable: false, value: 'ContractDateFrom' },
        { text: 'Дата (по)', align: 'center', sortable: false, value: 'ContractDateTo' },
        { text: 'Действия', align: 'center', sortable: false, value: 'actions' }
      ]
    }
  },
  methods: {
    hasGroup(organizationName) {
      // Check if any organization with this name has a GroupName set
      return this.organizations.some(o => o.OrganizationName === organizationName && o.GroupName);
    },

    onDisbandGroupButtonClicked(groupName) {
      this.groupToDisband = groupName;
      this.disbandDialog = true;
    },

    submitDisband() {
      // Find the master organization ID for this group
      const master = this.organizations.find(o =>
          o.OrganizationName === this.groupToDisband && o.GroupName
      );

      if (!master) return;

      this.disbandLoading = true;
      ClientOrganizationService.disbandClientOrganizationGroup(master.Id)
          .then(() => {
            this.disbandDialog = false;
            this.showSuccessMessage = true;
            this.load();
          })
          .finally(() => {
            this.disbandLoading = false;
          });
    },

    formatDate(dateStr) {
      if (!dateStr) return '';
      return moment(dateStr).format('DD.MM.YYYY');
    },
    getBranchesCount(organizationName) {
      return this.organizations.filter(o => o.OrganizationName === organizationName).length;
    },
    getMasterBranchName(masterId) {
      const master = this.organizations.find(o => o.Id === masterId);
      return master ? master.UnitName : 'Неизвестно';
    },
    load() {
      this.loading = true;
      this.$store.dispatch(LAYOUT_LOADING_START);

      ClientOrganizationService.getClientOrganizations()
          .then(response => {
            let data = response.data || [];

            data.forEach(item => {
              let score = 0;
              if (!item.Inn) score++;
              if (!item.AccountNumber) score++;
              if (!item.Mfo) score++;
              if (!item.ContractNumber) score++;
              if (!item.ContractDateFrom) score++;
              if (!item.ContractDateTo) score++;
              if (!item.ContractSignerUnitId) score++;

              item.missingScore = score;
            });

            data.sort((a, b) => b.missingScore - a.missingScore);
            this.organizations = data;
          })
          .finally(() => {
            this.loading = false;
            this.$store.dispatch(LAYOUT_LOADING_STOP);
          });
    },
    onEditButtonClicked(item) {
      this.editDialog = true;
      this.$nextTick(() => {
        this.$refs.editOrganization.set(item);
      });
    },
    onEditCanceled() {
      this.editDialog = false;
    },
    onEditSuccess() {
      this.showSuccessMessage = true;
      this.editDialog = false;
      this.load();
    },

    // --- JOIN BRANCHES LOGIC ---
    onJoinButtonClicked(organizationName) {
      this.currentJoinGroup = this.organizations.filter(o => o.OrganizationName === organizationName);
      this.joinForm = {
        masterBranchId: null,
        branchIdsToJoin: [],
        groupName: ''
      };
      this.joinDialog = true;
    },
    submitJoin() {
      this.joinLoading = true;

      const payload = {
        MasterBranchId: this.joinForm.masterBranchId,
        BranchIdsToJoin: this.joinForm.branchIdsToJoin,
        GroupName: this.joinForm.groupName
      };

      ClientOrganizationService.joinClientOrganizations(payload)
          .then(() => {
            this.joinDialog = false;
            this.showSuccessMessage = true;
            this.load();
          })
          .finally(() => {
            this.joinLoading = false;
          });
    },

    // --- UNJOIN BRANCH LOGIC ---
    onUnjoinButtonClicked(item) {
      this.branchToUnjoin = item;
      this.unjoinDialog = true;
    },
    submitUnjoin() {
      if (!this.branchToUnjoin) return;

      this.unjoinLoading = true;

      ClientOrganizationService.unjoinClientOrganization(this.branchToUnjoin.Id)
          .then(() => {
            this.unjoinDialog = false;
            this.showSuccessMessage = true;
            this.branchToUnjoin = null;
            this.load();
          })
          .finally(() => {
            this.unjoinLoading = false;
          });
    }
  },
  created() {
    this.load();
  }
}
</script>

<style scoped>
.group-header-row {
  background-color: #f0f4f8;
  cursor: pointer;
  transition: background-color 0.2s;
}
.group-header-row:hover {
  background-color: #e2e8f0;
}
</style>